// ViewBannerGroup.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig';

const ViewBannerGroup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('blockId');

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState(null); // API: result

  const fmtBool = (v) => (v ? 'Yes' : 'No');

  const fmtDateTime = (s) => {
    // API already returns "YYYY-MM-DD HH:mm:ss"
    if (!s) return '-';
    return s;
  };

  const safe = (v) => (v === null || v === undefined || v === '' ? '-' : v);

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMsg('');

      const res = await axiosInstance.get(
        `/internal-api/v1/client-web/cms/page/page-blocks/${id}/view`
      );

      if (!res?.data?.status) {
        setErrorMsg(res?.data?.error?.message || 'Failed to load banner group');
        setData(null);
        return;
      }

      setData(res.data.result); // {id, sectionType..., data:{...}}
    } catch (err) {
      console.error('ViewBannerGroup GET error:', err);
      setErrorMsg(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Request failed'
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const top = useMemo(() => {
    const d = data?.data || {};
    return {
      title: d.title,
      subTitle: d.subTitle,
      content: d.content,
      viewMoreLink: d.viewMoreLink,
      viewMoreLinkPageId: d.viewMoreLinkPageId,
      apiEndpointName: d.apiEndpointName,
      isActive: d.isActive,
      isVisible: d.isVisible,
      isPublic: d.isPublic,
      thumbnail: d.thumbnail,
      carousels: Array.isArray(d.carousels) ? d.carousels : []
    };
  }, [data]);

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="text-gray-700">Loading...</div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="mb-3 text-red-600">{errorMsg}</div>
        <button
          type="button"
          onClick={fetchData}
          className="px-4 py-2 text-white bg-blue-600 rounded-md"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="text-gray-700">No data found.</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          View Banner Group #{data.id}
        </h2>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchData}
            className="px-4 py-2 text-white bg-blue-600 rounded-md"
          >
            Refresh
          </button>

          <button
            type="button"
            onClick={() => navigate('/LandingPage')}
            className="px-4 py-2 text-white bg-gray-700 rounded-md"
          >
            Back
          </button>
        </div>
      </div>

      {/* Meta info */}
      <div className="grid grid-cols-1 gap-3 mb-6 md:grid-cols-2">
        <InfoRow label="Section Type" value={safe(data.sectionType)} />
        <InfoRow label="Display Order" value={safe(data.displayOrder)} />
        <InfoRow label="Is Active (Block)" value={fmtBool(!!data.isActive)} />
        <InfoRow label="Version" value={safe(data.version)} />
        <InfoRow label="Created At" value={fmtDateTime(data.createdAt)} />
        <InfoRow label="Updated At" value={fmtDateTime(data.updatedAt)} />
      </div>

      {/* Data section */}
      <div className="p-4 border rounded-md">
        <h3 className="mb-3 text-lg font-medium text-gray-800">Block Data</h3>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* <InfoRow label="Title" value={safe(top.title)} /> */}
          {/* <InfoRow label="Sub Title" value={safe(top.subTitle)} /> */}
          <InfoRow label="Content" value={safe(top.content)} />
          <InfoRow label="View More Link" value={safe(top.viewMoreLink)} />
          {/* <InfoRow label="View More Link Page Id" value={safe(top.viewMoreLinkPageId)} /> */}
          {/* <InfoRow label="API Endpoint Name" value={safe(top.apiEndpointName)} /> */}
          <InfoRow label="Is Active (Data)" value={fmtBool(!!top.isActive)} />
          <InfoRow label="Is Visible" value={fmtBool(!!top.isVisible)} />
          <InfoRow label="Is Public" value={fmtBool(!!top.isPublic)} />
        </div>

        {/* Thumbnail */}
        {/* <div className="mt-4">
          <h4 className="mb-2 text-sm font-semibold text-gray-700">Thumbnail</h4>
          {top.thumbnail ? (
            <pre className="p-3 overflow-auto text-xs border rounded-md bg-gray-50">
              {JSON.stringify(top.thumbnail, null, 2)}
            </pre>
          ) : (
            <div className="text-sm text-gray-600">No thumbnail</div>
          )}
        </div> */}
      </div>

      {/* Carousels */}
      <div className="mt-6 space-y-4">
        <h3 className="text-lg font-medium text-gray-800">Banners</h3>

        {top.carousels.length === 0 ? (
          <div className="text-sm text-gray-600">No carousels found.</div>
        ) : (
          top.carousels.map((carousel, cIndex) => (
            <div key={carousel.id || cIndex} className="p-4 border rounded-md">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-base font-semibold text-gray-800">
                  Banner Group #{carousel.id ?? cIndex + 1}
                </h4>
                <div className="text-sm text-gray-600">
                  {/* Display Order: {safe(carousel.displayOrder)} */}
                   {/* | Align Order:{' '} {safe(carousel.alignOrder)} */}
                </div>
              </div>

              {/* Each carousel has bannerGroups[] (your banners) */}
              {Array.isArray(carousel.bannerGroups) && carousel.bannerGroups.length > 0 ? (
                carousel.bannerGroups.map((bg, bgIndex) => (
                  <div
                    key={bg.id || bgIndex}
                    className="p-4 mb-4 border rounded-md bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-semibold text-gray-800">
                        Banner #{bgIndex + 1} (ID: {bg.id})
                      </h5>
                      <div className="text-xs text-gray-600">
                        Group Order: {safe(bg.groupOrder)} | Action Button:{' '}
                        {fmtBool(!!bg.isActionButtonEnabled)}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                      <InfoRow label="Main Title" value={safe(bg.mainTitle)} />
                      <InfoRow label="Sub Title" value={safe(bg.subTitle)} />
                      <InfoRow label="Short Description" value={safe(bg.shortDescription)} />
                      <InfoRow label="Description" value={safe(bg.description)} />
                    </div>

                    <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                      <InfoRow label="Banner Group Id" value={safe(bg.bannerGroupId)} />
                      <InfoRow label="Component" value={safe(bg.component)} />
                    </div>

                    {/* Action Button object */}
                    <div className="mt-3">
                      <h6 className="mb-1 text-xs font-semibold text-gray-700">
                        Action Button
                      </h6>
                      {bg.actionButton ? (
                        <pre className="p-3 overflow-auto text-xs bg-white border rounded-md">
                          {JSON.stringify(bg.actionButton, null, 2)}
                        </pre>
                      ) : (
                        <div className="text-sm text-gray-600">No action button</div>
                      )}
                    </div>

                    {/* Contents */}
                    <div className="mt-4">
                      <h6 className="mb-2 text-xs font-semibold text-gray-700">Contents</h6>

                      {Array.isArray(bg.contents) && bg.contents.length > 0 ? (
                        <div className="space-y-4">
                          {bg.contents.map((ct, ctIndex) => (
                            <div key={ct.id || ctIndex} className="p-3 bg-white border rounded-md">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold text-gray-800">
                                  Content #{ctIndex + 1} (ID: {ct.id})
                                </div>
                                <div className="text-xs text-gray-600">
                                  Display Order: {safe(ct.displayOrder)} | Clickable:{' '}
                                  {fmtBool(!!ct.isClickable)}
                                </div>
                              </div>

                              <div className="grid grid-cols-1 gap-3 mt-2 md:grid-cols-2">
                                <InfoRow label="Alt" value={safe(ct.alt)} />
                                <InfoRow label="Click Link" value={safe(ct.clickLink)} />
                                <InfoRow label="Valid From" value={fmtDateTime(ct.validFrom)} />
                                <InfoRow label="Valid Till" value={fmtDateTime(ct.validTill)} />
                              </div>

                              {/* Images */}
                              <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                                <ImageCard
                                  title="Web Image"
                                  url={ct.webImgPath}
                                  meta={`${safe(ct.webImgName)}.${safe(ct.webImgExtension)}`}
                                />
                                <ImageCard
                                  title="Mobile Image"
                                  url={ct.mobImgPath}
                                  meta={`${safe(ct.mobImgName)}.${safe(ct.mobImgExtension)}`}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600">No contents</div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-600">No bannerGroups in this carousel.</div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="p-3 border rounded-md">
    <div className="text-xs font-semibold text-gray-600">{label}</div>
    <div className="text-sm text-gray-900 break-words">{value}</div>
  </div>
);

const ImageCard = ({ title, url, meta }) => {
  return (
    <div className="p-3 border rounded-md">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-gray-700">{title}</div>
        <div className="text-[10px] text-gray-500">{meta}</div>
      </div>

      {url ? (
        <>
          {/* Use normal img tag - the URLs are absolute from API */}
          <img
            src={url}
            alt={title}
            className="object-cover w-full h-48 border rounded-md"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <div className="mt-2 text-xs text-blue-700 break-all">{url}</div>
        </>
      ) : (
        <div className="text-sm text-gray-600">No image</div>
      )}
    </div>
  );
};

export default ViewBannerGroup;
